import React, { useState } from "react";

import { Flex, Price, LegacyText, Text, LinkText } from "flicket-ui";
import groupBy from "lodash/groupBy";
import map from "lodash/map";

import { LineItemType, OrderQuery } from "~graphql/sdk";
import { useOrganization } from "~hooks";
import { formatTicketName } from "~lib/helpers/formatTicketName";

type LineItem = OrderQuery["order"]["lineItems"]["edges"][number]["node"];

type TicketFeeProps = {
  lineItems: LineItem[];
};

export const TicketFees = ({ lineItems }: TicketFeeProps) => {
  const { organization } = useOrganization();
  const [showDetails, setShowDetails] = useState(false);
  const sum = lineItems.reduce(
    (acc, curr) =>
      acc +
      (curr.ticketFeeAmount +
        (curr.stadiumLevyFee ?? 0) +
        (curr.customTaxAmount ?? 0)) *
        curr.quantity,
    0
  );

  // We shouldn't need the dropdown details if no service details
  const hideServiceFeeDetails = sum <= 0;
  if (hideServiceFeeDetails) return null;

  return (
    <>
      <Flex w="100%" justifyContent="space-between" mt={3}>
        <Flex flexDir="column">
          <Flex alignItems="baseline" mb={showDetails ? 3 : 0}>
            <LegacyText
              fontSize={3}
              lineHeight="normal"
              fontWeight="demiBold"
              color="N800"
              textTransform="capitalize"
            >
              Ticket fees
            </LegacyText>
            <Text variant="small" ml="1/2">
              <LinkText onClick={() => setShowDetails(!showDetails)}>
                {showDetails ? "Hide" : "Show"} details
              </LinkText>
            </Text>
          </Flex>
        </Flex>
        {!showDetails && (
          <Price
            color="N800"
            fontSize={3}
            lineHeight="normal"
            price={sum}
            currency={organization.currency}
          />
        )}
      </Flex>
      {showDetails && <TicketFeeDetails lineItems={lineItems} />}
    </>
  );
};

function TicketFeeDetails({ lineItems }: { lineItems: LineItem[] }) {
  const { organization } = useOrganization();
  const groupedByFee = groupBy(
    lineItems,
    (lineItem) =>
      lineItem?.membershipType?.membership?.name ||
      formatTicketName(lineItem?.ticketType?.name, lineItem?.pricingTierName) ||
      (lineItem.type === LineItemType.Addon && lineItem.name) ||
      lineItem?.pointItem?.name
  );

  /** @note prefering lodash map over Object.values(groupedByFee) as the key gives us info we need (and we already include lodash for the group) */
  return (
    <>
      {map(groupedByFee, (ticketGroup, key) => (
        <Flex
          justifyContent="space-between"
          w="100%"
          key={ticketGroup[0].id}
          mb="6/4"
        >
          <Flex flexDir="column">
            <LegacyText
              fontSize={3}
              lineHeight="normal"
              fontWeight="demiBold"
              color="N800"
              textTransform="capitalize"
              mb="1/2"
            >
              {key}
            </LegacyText>
            {ticketGroup[0].ticketFeeAmount > 0 && (
              <Flex alignItems="baseline" mt="1/4">
                <LegacyText
                  color="N600"
                  fontSize={1}
                  lineHeight={"1.2" as any}
                  whiteSpace="nowrap"
                  mr="1/4"
                >
                  {ticketGroup[0].quantity > 1
                    ? ticketGroup[0].quantity
                    : ticketGroup.length}
                  x
                </LegacyText>
                <Price
                  color="N800"
                  fontSize={3}
                  lineHeight="normal"
                  price={ticketGroup[0].ticketFeeAmount}
                  currency={organization.currency}
                />
                <LegacyText
                  color="N600"
                  fontSize={1}
                  lineHeight={"1.2" as any}
                  whiteSpace="nowrap"
                  mr="1/4"
                  ml="1/4"
                >
                  - Per Ticket Booking Fee
                </LegacyText>
              </Flex>
            )}
            {ticketGroup[0].stadiumLevyFee > 0 && (
              <Flex alignItems="flex-end" mt="1/4">
                <LegacyText
                  color="N600"
                  fontSize={1}
                  lineHeight={"1.2" as any}
                  whiteSpace="nowrap"
                  mr="1/4"
                >
                  {ticketGroup[0].quantity > 1
                    ? ticketGroup[0].quantity
                    : ticketGroup.length}
                  x
                </LegacyText>
                <Price
                  color="N800"
                  fontSize={3}
                  lineHeight="normal"
                  price={ticketGroup[0].stadiumLevyFee}
                  currency={organization.currency}
                />
                <LegacyText
                  color="N600"
                  fontSize={1}
                  lineHeight={"1.2" as any}
                  whiteSpace="nowrap"
                  mr="1/4"
                  ml="1/4"
                >
                  - Stadium Levy Fee
                </LegacyText>
              </Flex>
            )}
            {ticketGroup[0].customTaxAmount > 0 && (
              <Flex alignItems="flex-end" mt="1/4">
                <LegacyText
                  color="N600"
                  fontSize={1}
                  lineHeight={"1.2" as any}
                  whiteSpace="nowrap"
                  mr="1/4"
                >
                  {ticketGroup[0].quantity > 1
                    ? ticketGroup[0].quantity
                    : ticketGroup.length}
                  x
                </LegacyText>
                <Price
                  color="N800"
                  fontSize={3}
                  lineHeight="normal"
                  price={ticketGroup[0].customTaxAmount}
                  currency={organization.currency}
                />
                <LegacyText
                  color="N600"
                  fontSize={1}
                  lineHeight={"1.2" as any}
                  whiteSpace="nowrap"
                  mr="1/4"
                  ml="1/4"
                >
                  - {organization.customTaxName ?? "Additional tax"}
                </LegacyText>
              </Flex>
            )}
          </Flex>
          <LegacyText fontSize={1} lineHeight="normal">
            <Price
              color="N800"
              fontSize={3}
              lineHeight="normal"
              price={
                (ticketGroup[0].ticketFeeAmount +
                  (ticketGroup[0].stadiumLevyFee
                    ? ticketGroup[0].stadiumLevyFee
                    : 0) +
                  (ticketGroup[0].customTaxAmount ?? 0)) *
                (ticketGroup[0].quantity > 1
                  ? ticketGroup[0].quantity
                  : ticketGroup.length)
              }
              currency={organization.currency}
            />
          </LegacyText>
        </Flex>
      ))}
    </>
  );
}
